import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { env } from '#lib';
import type { SearchToken } from '../types';
import { stringifySearchTokens, tokenizeSearchString } from '../utils';

type State = {
  searchString: string;
  searchTokens: SearchToken[];
};

type Actions = {
  appendTag: (fullName: string, id: string) => void;
  commit: () => void;
  init: (value: string) => void;
  reset: () => void;
  setSearchString: (value: string) => void;
};

type SearchStore = State & Actions;

const DEFAULT_STATE: State = {
  searchString: '',
  searchTokens: []
};

export const useSearchStore = create<SearchStore>()(
  devtools(
    (set, get) => {
      return {
        ...DEFAULT_STATE,

        // Append to search tokens (e.g., tag click from results)
        appendTag: (fullName, id) => {
          const { searchTokens } = get();

          const tokenExists = searchTokens.some(token => {
            return token.type === 'tag' && token.fullName === fullName;
          });

          if (tokenExists) return;

          const newTag: SearchToken = { type: 'tag', fullName, id };
          const newSearchTokens = [...searchTokens, newTag];

          set({
            searchTokens: newSearchTokens,
            searchString: stringifySearchTokens(newSearchTokens)
          });
        },

        commit: () => {
          const { searchString } = get();
          const tokens = tokenizeSearchString(searchString);
          set({ searchTokens: tokens });
        },

        // Initialize both search string and search tokens (e.g., from URL)
        init: searchString => {
          set({
            searchString,
            searchTokens: tokenizeSearchString(searchString)
          });
        },

        reset: () => {
          set(DEFAULT_STATE);
        },

        setSearchString: value => {
          set({ searchString: value });
        }
      };
    },
    {
      name: 'useSearchStore',
      enabled: env.isDev()
    }
  )
);
